import { api } from '@/shared/axios-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

interface RestoreResponse {
  success?: boolean;
  message: string;
  status?: 'restored';
  instrumentation_jobs?: string[];
  instrumentation_targets?: string[];
}

function restoreErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail ?? err.response?.data?.message;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object') {
      try {
        return JSON.stringify(detail);
      } catch {
        /* fall through */
      }
    }
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Restore failed';
}

/** Restore a soft-deleted onboarded branch card and re-queue instrumentation PRs. */
const restoreComponent = async (branchId: string): Promise<RestoreResponse> => {
  if (!branchId?.trim()) {
    throw new Error('Missing branchId — cannot restore this component card.');
  }
  try {
    const { data } = await api.post<RestoreResponse>(
      `/api/repositories/${branchId}/restore`,
    );
    return data;
  } catch (err) {
    throw new Error(restoreErrorMessage(err));
  }
};

export const useRestoreComponent = () => {
  const queryClient = useQueryClient();
  return useMutation<RestoreResponse, Error, string>({
    mutationFn: restoreComponent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['component-list'] });
    },
  });
};
